import { useCallback, useEffect, useState } from 'react'
import { ShieldAlert, Download, RefreshCw } from 'lucide-react'
import { useApi } from '../hooks/useApi'

/**
 * カバレッジ監査ビュー (管理者のみ)
 * 「受話可能なオペレーターがゼロだった期間」の一覧。
 * データはカバレッジ評価 Lambda (毎分) が記録した COVERAGE_GAPS。
 */

interface CoverageGap {
  organizationId: string
  gapStart: number
  gapEnd?: number
  durationMs?: number
}

function toInputValue(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDuration(ms: number): string {
  const totalMinutes = Math.round(ms / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}時間${minutes}分`
  return `${minutes}分`
}

export default function CoverageAuditView() {
  const api = useApi()
  const [fromDate, setFromDate] = useState(() => toInputValue(Date.now() - 7 * 24 * 60 * 60 * 1000))
  const [toDate, setToDate] = useState(() => toInputValue(Date.now()))
  const [gaps, setGaps] = useState<CoverageGap[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchGaps = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const from = new Date(`${fromDate}T00:00:00`).getTime()
      const to = new Date(`${toDate}T23:59:59.999`).getTime()
      const data = await api.get<{ gaps: CoverageGap[] }>(`/api/presence/coverage-gaps?from=${from}&to=${to}`)
      setGaps(data.gaps || [])
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [api, fromDate, toDate])

  useEffect(() => {
    fetchGaps()
    // 初回のみ自動取得。以降は「更新」ボタン
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportCsv = () => {
    const header = '開始,終了,継続時間(分)\n'
    const rows = gaps.map((g) => {
      const end = typeof g.gapEnd === 'number' ? formatDateTime(g.gapEnd) : '継続中'
      const minutes = typeof g.gapEnd === 'number' ? Math.round((g.gapEnd - g.gapStart) / 60000) : ''
      return `${formatDateTime(g.gapStart)},${end},${minutes}`
    }).join('\n')
    const blob = new Blob(['﻿' + header + rows], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `coverage-gaps_${fromDate}_${toDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ShieldAlert className="w-5 h-5" />
          カバレッジ監査
        </h2>
        <button
          onClick={exportCsv}
          disabled={gaps.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        受話可能なオペレーター (受付中・通話中) がゼロだった期間の記録です。毎分の評価に基づくため 1 分程度の誤差があります。
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">開始日</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">終了日</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>
        <button
          onClick={fetchGaps}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          更新
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {!loading && gaps.length === 0 && !error && (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400 text-sm">
          この期間に「誰も受話できない状態」は記録されていません
        </div>
      )}

      {gaps.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4 font-medium">開始</th>
                <th className="py-2 pr-4 font-medium">終了</th>
                <th className="py-2 pr-4 font-medium">継続時間</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((g) => {
                const ongoing = typeof g.gapEnd !== 'number'
                return (
                  <tr key={g.gapStart} className="border-b border-gray-100 dark:border-gray-700/60">
                    <td className="py-2 pr-4 text-gray-900 dark:text-white tabular-nums">{formatDateTime(g.gapStart)}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {ongoing ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                          継続中
                        </span>
                      ) : (
                        <span className="text-gray-900 dark:text-white">{formatDateTime(g.gapEnd as number)}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-gray-900 dark:text-white tabular-nums">
                      {ongoing ? formatDuration(Date.now() - g.gapStart) + '〜' : formatDuration((g.gapEnd as number) - g.gapStart)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
